import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { statusLabel } from "@wms/domain";
import type { ActionMeta, ActionResult } from "@wms/domain";
import { Alert, App, Button, Card, Descriptions, Form, Grid, InputNumber, Modal, Segmented, Space, Table, Tag, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { ApiError, api, describeError } from "../api/client";
import type { Page } from "../api/client";
import { formatQty } from "../api/format";
import { useAuthStore } from "../auth/store";
import { outcomeUnknown } from "./formProblem";
import { RefLabel, RefSelect } from "./RefSelect";

interface Line {
  id: string;
  line_no: number;
  storage_object_id: string;
  location_id?: string | null;
  state_id?: string | null;
  batch_id?: string | null;
  package_id?: string | null;
  packages_count?: string | null;
  quantity_plan: string;
  quantity_fact?: string | null;
  quantity_shipped?: string;
}

interface StockRow {
  id: string;
  storage_object_id: string;
  location_id: string;
  batch_id: string | null;
  batch_code?: string | null;
  quantity: string;
  planned_out: string;
  available: string | null;
  /** Why this stock is not offered (expired batch, quarantine), QA20-011. */
  blocked?: string | null;
}

interface PackageRow {
  id: string;
  storage_object_id: string;
  name: string;
  ratio: string;
}

/** GET /expected_receipts/{id}/receive-places: the cell the storage conditions of a line choose for it. */
interface ReceivePlace {
  line_id: string;
  location_id: string | null;
  place: string | null;
  /** The product's temperature, «+2…+8 °C»; null when it has no regime. */
  need: string | null;
  /** Why no cell fits, when none does. */
  why: string | null;
}

interface Props {
  entity: string;
  id: string;
  action: ActionMeta;
  /** The document row: the warehouse narrows the stock lookup. */
  row?: Record<string, unknown>;
  onClose: () => void;
  /**
   * UXW-59 (option «Панель действий»): a lost answer is checked against the fact of the lines before anything is
   * offered — a receipt or a shipment is never suggested again only because the answer did not come.
   */
  verify?: boolean;
}

/** «0,5», «1 250» — never «0.5» (QA1101-B02: the numbers of the dialog are written as people write them). */
const fmt = (value: string | number | null | undefined) => formatQty(value ?? 0);

interface ProductRow {
  id: string;
  code: string;
  name: string;
  unit_id?: string | null;
}

interface UnitRow {
  id: string;
  name: string;
  code?: string;
  decimals?: number | null;
}

/** The unit of a product and its precision: «шт», 0 digits; «кг», 3 digits (BUG-R31-010 on the server). */
export interface LineUnit {
  name: string;
  decimals: number;
}

/**
 * QA1101-B03: why a quantity does not fit the unit of its product, said at the line before anything is posted —
 * «Стаканы» are counted in pieces, so 1,5 is refused with the way to fix it; kilograms take their digits. The value
 * is never rounded or changed: the person corrects it. Null — it fits (or the unit is not known yet).
 */
export function unitProblem(quantity: number | null | undefined, product: string, unit: LineUnit | undefined): string | null {
  if (quantity === null || quantity === undefined || !unit || !Number.isFinite(quantity)) return null;
  const [, frac = ""] = String(quantity).split(".");
  if (frac.length <= unit.decimals) return null;
  if (unit.decimals <= 0) {
    const counted = unit.name === "шт" ? "в штуках" : `в «${unit.name}»`;
    return `«${product}» учитывается ${counted}. Введите целое количество`;
  }
  return `«${product}» учитывается в «${unit.name}»: не больше ${unit.decimals} знаков после запятой`;
}

function plural(n: number, one: string, few: string, many: string): string {
  const tail = n % 10;
  const tail2 = n % 100;
  if (tail === 1 && tail2 !== 11) return one;
  if (tail >= 2 && tail <= 4 && (tail2 < 12 || tail2 > 14)) return few;
  return many;
}

/**
 * QA0211-B01: the stock of a location for the choice «Откуда отгружаем», per product in its own unit: one product —
 * «на складе 8 шт, можно взять 8 шт»; several — «2 товара: CUP31 8 шт; TEA31 0,5 кг (можно взять 0,25 кг)», the
 * available quantity said where it is less than the stock.
 */
export function locationStock(
  products: Record<string, { quantity: number; available: number }>,
  qtyText: (value: number, objectId: string) => string,
  codeOf: (objectId: string) => string,
): string {
  const entries = Object.entries(products);
  if (entries.length === 1) {
    const [objectId, v] = entries[0];
    return `на складе ${qtyText(v.quantity, objectId)}, можно взять ${qtyText(v.available, objectId)}`;
  }
  const parts = entries.map(([objectId, v]) => {
    const free = v.available < v.quantity ? ` (можно взять ${qtyText(v.available, objectId)})` : "";
    return `${codeOf(objectId)} ${qtyText(v.quantity, objectId)}${free}`;
  });
  return `${entries.length} ${plural(entries.length, "товар", "товара", "товаров")}: ${parts.join("; ")}`;
}

/**
 * Receive / ship / count by document lines. Three screens: enter quantities, confirm what will be posted,
 * read the result (what moved, from which batches, what to do next) — section 6 of the test report.
 */
export function LinesActionModal({ entity, id, action, row, onClose, verify }: Props) {
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const [quantities, setQuantities] = useState<Record<string, number | null>>({});
  const [locationId, setLocationId] = useState<string | undefined>();
  const [step, setStep] = useState<"edit" | "confirm" | "done">("edit");
  const [result, setResult] = useState<ActionResult | null>(null);
  // BUG-R31-011: a refused attempt is kept as history of that attempt, never shown as the error of the new input
  const [refused, setRefused] = useState<{ text: string; location?: string } | null>(null);
  const isShip = action.code === "ship";
  const needsLocation = action.needs_location !== false;
  // UXW-59: what the check of a lost answer found, and the fact of the lines when the request left
  const [lost, setLost] = useState<"checking" | "done" | "unchanged" | "offline" | null>(null);
  const [factBefore, setFactBefore] = useState<string | null>(null);
  // BUG-010: below 768 px the lines are cards — product, plan and the quantity field one under another — instead of
  // a seven-column table that pushes the quantity off the screen and drags the whole dialog sideways
  const screens = Grid.useBreakpoint();
  const narrow = screens.md === false;

  const lines = useQuery({
    queryKey: [action.lines_entity, "for-action", id],
    queryFn: () => api.get<Page<Line>>(`/${action.lines_entity}`, { size: 500, sort: "line_no", filter: { field: action.lines_fk, op: "eq", value: id } }),
  });
  const objectIds = useMemo(() => [...new Set((lines.data?.items ?? []).map((l) => l.storage_object_id))], [lines.data]);
  // QA1101-B02: the products of these lines with their units — every number of the dialog says «шт» or «кг»
  const objects = useQuery({
    queryKey: ["storage_objects", "for-lines", objectIds],
    queryFn: () => api.get<Page<ProductRow>>("/storage_objects", { size: 500, filter: { field: "id", op: "in", value: objectIds } }),
    enabled: objectIds.length > 0,
    staleTime: 60_000,
  });
  const units = useQuery({
    queryKey: ["units", "all"],
    queryFn: () => api.get<Page<UnitRow>>("/units", { size: 500 }),
    staleTime: 300_000,
  });
  const option = useAuthStore((s) => s.option);
  // the same rule the server applies with «Строгая проверка документов»; with «Запрет недопустимого ввода» too
  const checkUnits = option("use_strict_validation") || option("use_input_guard");
  // QA0211 (option «Запрет недопустимого ввода»): a count line starts empty — «ещё не пересчитано» — and the book
  // quantity is put in only by «Подставить учётное»; without the option the book quantity is offered as before
  const countBlank = !needsLocation && option("use_input_guard");
  const packages = useQuery({
    queryKey: ["packages", "for-lines", objectIds],
    queryFn: () => api.get<Page<PackageRow>>("/packages", { size: 500, filter: { field: "storage_object_id", op: "in", value: objectIds } }),
    enabled: objectIds.length > 0,
  });
  // WMS-01: the location choice is built from the stock the cards show, not from every cell of the warehouse
  const stock = useQuery({
    queryKey: ["stock", "for-ship", id, objectIds],
    // the server applies the same rules as the shipment itself: expired and quarantined stock is not «можно взять»
    queryFn: () => api.get<Page<StockRow>>(`/${entity}/${id}/stock`),
    enabled: isShip && objectIds.length > 0,
  });

  // receiving: the places of the warehouse's receiving zones come first and the first of them is chosen — the list is
  // otherwise in walking order, storage cells first, and a reagent «2–8 °C» was offered an ordinary rack cell
  const receivingPlaces = useQuery({
    queryKey: ["locations", "receiving-places", row?.warehouse_id],
    queryFn: async () => {
      const zones = await api.get<Page<{ id: string }>>("/zones", {
        size: 200,
        filter: { and: [{ field: "warehouse_id", op: "eq", value: row?.warehouse_id }, { field: "kind", op: "eq", value: "receiving" }] },
      });
      const ids = zones.items.map((z) => z.id);
      if (!ids.length) return [];
      const places = await api.get<Page<{ id: string }>>("/locations", { size: 200, sort: "sort_order,code", filter: { field: "zone_id", op: "in", value: ids } });
      return places.items;
    },
    enabled: !isShip && needsLocation && !!row?.warehouse_id,
  });
  // receiving by storage conditions: every line gets its own cell of a storage zone — a reagent «2–8 °C» the cold
  // room, tips a rack — instead of one cell for the whole delivery that refuses half of it
  const canSpread = !isShip && needsLocation && entity === "expected_receipts" && action.code === "receive";
  const [byConditions, setByConditions] = useState(true);
  const spread = canSpread && byConditions;
  const [lineLocations, setLineLocations] = useState<Record<string, string | undefined>>({});
  const places = useQuery({
    queryKey: ["receive-places", id],
    queryFn: () => api.get<{ items: ReceivePlace[] }>(`/${entity}/${id}/receive-places`),
    enabled: canSpread,
  });
  const placeOf = useMemo(() => Object.fromEntries((places.data?.items ?? []).map((p) => [p.line_id, p])), [places.data]);
  useEffect(() => {
    const items = places.data?.items;
    if (!items) return;
    // nothing fits by conditions (a warehouse without storage zones): one cell, as before
    if (!items.some((p) => p.location_id)) setByConditions(false);
    setLineLocations((prev) => {
      const next = { ...prev };
      for (const p of items) if (next[p.line_id] === undefined && p.location_id) next[p.line_id] = p.location_id;
      return next;
    });
  }, [places.data]);
  const destination = (line: Line) => (spread ? lineLocations[line.id] : locationId);

  const receivingPrefer = useMemo(
    () => Object.fromEntries((receivingPlaces.data ?? []).map((p) => [String(p.id), "рекомендуется"])),
    [receivingPlaces.data],
  );
  useEffect(() => {
    if (!isShip && !locationId && receivingPlaces.data?.length) setLocationId(String(receivingPlaces.data[0].id));
  }, [isShip, locationId, receivingPlaces.data]);

  useEffect(() => {
    if (!lines.data) return;
    const initial: Record<string, number | null> = {};
    for (const line of lines.data.items) {
      if (!needsLocation) {
        initial[line.id] = (line.quantity_fact === null || line.quantity_fact === undefined) && !countBlank ? Number(line.quantity_plan) : null;
        continue;
      }
      const done = Number(line.quantity_fact ?? line.quantity_shipped ?? 0);
      const rest = Number(line.quantity_plan) - done;
      initial[line.id] = rest > 0 ? rest : null;
    }
    setQuantities(initial);
  }, [lines.data, needsLocation, countBlank]);

  const packageOf = (line: Line) => (line.package_id ? packages.data?.items.find((p) => p.id === line.package_id) : undefined);
  const productOf = (objectId: string) => objects.data?.items.find((r) => String(r.id) === objectId);
  const objectLabel = (objectId: string) => {
    const o = productOf(objectId);
    return o ? `${o.code} — ${o.name}` : "товар";
  };
  const unitOf = (objectId: string): LineUnit | undefined => {
    const unitId = productOf(objectId)?.unit_id;
    const u = unitId ? units.data?.items.find((r) => r.id === unitId) : undefined;
    return u ? { name: String(u.name ?? u.code ?? ""), decimals: Number(u.decimals ?? 0) } : undefined;
  };
  /** QA0211: a count line without a saved fact is «ещё не пересчитано», never «0» — a real zero is a counted value. */
  const doneText = (line: Line) =>
    !needsLocation && (line.quantity_fact === null || line.quantity_fact === undefined)
      ? "ещё не пересчитано"
      : qtyText(line.quantity_fact ?? line.quantity_shipped ?? null, line.storage_object_id);
  /** «40 шт», «0,5 кг»; the bare number while the unit is loading. */
  const qtyText = (value: string | number | null | undefined, objectId: string) => {
    const unit = unitOf(objectId)?.name;
    return unit ? `${fmt(value)} ${unit}` : fmt(value);
  };

  /**
   * Per location: how much of the document's goods can be taken there (hint in the selector). QA0211-B01: summed per
   * product — the batches of one product add up, pieces and kilograms of different products never do («8,5» was
   * 8 шт and 0,5 кг).
   */
  const stockByLocation = useMemo(() => {
    const out: Record<string, { products: Record<string, { quantity: number; available: number }>; blocked: string[]; rows: StockRow[] }> = {};
    // the products in the order of the document lines, as the confirmation lists them
    const rank = (objectId: string) => objectIds.indexOf(objectId);
    const rows = [...(stock.data?.items ?? [])].sort((a, b) => rank(a.storage_object_id) - rank(b.storage_object_id));
    for (const s of rows) {
      const entry = (out[s.location_id] ??= { products: {}, blocked: [], rows: [] });
      const product = (entry.products[s.storage_object_id] ??= { quantity: 0, available: 0 });
      product.quantity += Number(s.quantity);
      product.available += Number(s.available ?? Number(s.quantity) - Number(s.planned_out));
      if (s.blocked) entry.blocked.push(s.blocked);
      entry.rows.push(s);
    }
    return out;
  }, [stock.data, objectIds]);
  const prefer = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(stockByLocation).map(([loc, v]) => [
          loc,
          `${locationStock(v.products, qtyText, (objectId) => productOf(objectId)?.code ?? "товар")}${v.blocked.length ? ` (не предлагается: ${v.blocked.join("; ")})` : ""}`,
        ]),
      ),
    // qtyText and productOf read the loaded products and units
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stockByLocation, objects.data, units.data],
  );

  const chosen = (lines.data?.items ?? []).filter((l) => {
    const q = quantities[l.id];
    return q !== null && q !== undefined && (q > 0 || !needsLocation);
  });
  // QA1101-B03: the refusal of the server for a line («строка №1: только целое число») stays at that line until its
  // quantity changes; the live check of the unit runs before «Проверить и продолжить»
  const [lineRefusals, setLineRefusals] = useState<Record<string, string>>({});
  const problemOf = (line: Line): string | null =>
    (checkUnits ? unitProblem(quantities[line.id], productOf(line.storage_object_id)?.name ?? "Товар", unitOf(line.storage_object_id)) : null) ??
    lineRefusals[line.id] ??
    null;
  const problems = chosen.filter((line) => problemOf(line));
  // QA0211: the count lines with neither a saved fact nor a typed value — «Подставить учётное» fills only these
  const uncounted = (lines.data?.items ?? []).filter(
    (l) => (l.quantity_fact === null || l.quantity_fact === undefined) && (quantities[l.id] === null || quantities[l.id] === undefined),
  );

  /** The fact of every line («12.5|0|3»): equal before and after — nothing was posted. */
  const factOf = (items: Line[] | undefined) => (items ?? []).map((l) => String(Number((isShip ? l.quantity_shipped : l.quantity_fact) ?? 0))).join("|");
  const checkLost = async () => {
    setLost("checking");
    try {
      const fresh = await lines.refetch({ throwOnError: true });
      const changed = factOf(fresh.data?.items) !== factBefore;
      if (changed) {
        queryClient.invalidateQueries({ queryKey: [entity] });
        queryClient.invalidateQueries({ queryKey: ["stock"] });
        queryClient.invalidateQueries({ queryKey: ["tasks"] });
      }
      setLost(changed ? "done" : "unchanged");
    } catch {
      setLost("offline");
    }
  };
  const submit = useMutation({
    mutationFn: () =>
      api.post<ActionResult>(`/${entity}/${id}/${action.code}`, {
        lines: chosen.map((line) => ({ line_id: line.id, quantity: quantities[line.id], location_id: destination(line) ?? undefined })),
      }),
    onSuccess: (data) => {
      setResult(data);
      setStep("done");
      queryClient.invalidateQueries({ queryKey: [entity] });
      queryClient.invalidateQueries({ queryKey: [action.lines_entity] });
      queryClient.invalidateQueries({ queryKey: ["stock"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["receive-places"] });
      queryClient.invalidateQueries({ queryKey: ["topology"] });
      message.success(`${action.label}: готово`);
    },
    onError: (e) => {
      if (verify && outcomeUnknown(e)) {
        // the dialog stays on the confirmation: the quantities and the cell are kept, nothing is offered yet
        void checkLost();
        return;
      }
      setRefused({ text: describeError(e), location: spread ? undefined : locationId });
      if (e instanceof ApiError && Array.isArray(e.problem.errors)) {
        const byNo = Object.fromEntries((lines.data?.items ?? []).map((l) => [l.line_no, l.id]));
        const next: Record<string, string> = {};
        for (const err of e.problem.errors as { line_no?: number; msg?: string }[]) {
          const lineId = err.line_no !== undefined ? byNo[err.line_no] : undefined;
          if (lineId && err.msg) next[lineId] = err.msg;
        }
        setLineRefusals(next);
      }
      setStep("edit");
    },
  });
  /** Any change of what will be posted makes the previous refusal history: the live error is cleared. */
  const changed = () => {
    if (submit.error) submit.reset();
  };

  const filled = chosen.length > 0;
  const hasColumn = (name: keyof Line) => (lines.data?.items ?? []).some((l) => l[name]);
  const stockRowsAtLocation = locationId ? stockByLocation[locationId]?.rows ?? [] : [];
  const lineDestination = (line: Line, width: number | string = "100%") => {
    const p = placeOf[line.id];
    return (
      <Space direction="vertical" size={2} style={{ width }}>
        <RefSelect
          entity="locations"
          value={lineLocations[line.id]}
          onChange={(v) => {
            setLineLocations({ ...lineLocations, [line.id]: v as string | undefined });
            changed();
          }}
          filter={row?.warehouse_id ? { field: "warehouse_id", op: "eq", value: row.warehouse_id } : null}
          placeholder="Ячейка для этой строки"
          aria-label={`Куда: строка ${line.line_no}`}
          style={{ width }}
        />
        {p?.why ? (
          <Typography.Text type="warning" style={{ fontSize: 12 }}>
            {p.why}
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {p?.need ? `товар хранится при ${p.need}` : "товар без температурного режима"}
          </Typography.Text>
        )}
      </Space>
    );
  };

  const editScreen = (
    <>
      {needsLocation && (
        <Form layout="vertical" disabled={false}>
          {canSpread && (
            <Form.Item label="Куда принимаем" required style={{ marginBottom: spread ? 12 : 8 }} extra={spread ? "Каждой строке подобрана ячейка зоны хранения по температурному режиму товара, правилам размещения и свободному месту. Ячейку строки можно поменять." : undefined}>
              <Segmented
                block={narrow}
                value={spread ? "conditions" : "one"}
                onChange={(v) => {
                  setByConditions(v === "conditions");
                  changed();
                }}
                options={[
                  { value: "conditions", label: narrow ? "По условиям" : "По условиям хранения товара" },
                  { value: "one", label: narrow ? "В одну ячейку" : "Всё в одну ячейку" },
                ]}
              />
            </Form.Item>
          )}
          {!spread && (
          <Form.Item
            label={isShip ? "Откуда отгружаем (ячейка с остатком)" : canSpread ? "Ячейка для всей поставки" : "Куда принимаем (ячейка приёмки)"}
            required
            tooltip={isShip ? "Показаны ячейки, где лежит товар заказа, с остатком и доступным количеством" : "Ячейка приёмки или любая активная ячейка, куда разрешено класть"}
            extra={isShip && stock.data && Object.keys(stockByLocation).length === 0 ? "На складе нет остатка по товарам этого заказа" : undefined}
          >
            <RefSelect
              entity="locations"
              value={locationId}
              onChange={(v) => {
                setLocationId(v as string | undefined);
                changed();
              }}
              prefer={isShip ? prefer : receivingPrefer}
              filter={row?.warehouse_id ? { field: "warehouse_id", op: "eq", value: row.warehouse_id } : null}
              placeholder="Введите полный код ячейки или его начало"
            />
          </Form.Item>
          )}
        </Form>
      )}
      {isShip && locationId && stockRowsAtLocation.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={`В этой ячейке: ${stockRowsAtLocation
            .map(
              (s) =>
                `${objectLabel(s.storage_object_id)}${s.batch_code ? ` (партия ${s.batch_code})` : s.batch_id ? " (партия)" : ""}: на складе ${qtyText(s.quantity, s.storage_object_id)}, отложено ${qtyText(s.planned_out, s.storage_object_id)}, можно взять ${qtyText(s.available ?? Number(s.quantity) - Number(s.planned_out), s.storage_object_id)}${s.blocked ? ` — ${s.blocked}` : ""}`,
            )
            .join("; ")}`}
        />
      )}
      {isShip && locationId && stock.data && stockRowsAtLocation.length === 0 && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="В выбранной ячейке нет остатка по товарам заказа — отгрузка из неё будет отклонена" />
      )}
      {countBlank && uncounted.length > 0 && (
        <Space wrap style={{ marginBottom: 12 }}>
          <Typography.Text type="secondary">Ещё не пересчитано строк: {uncounted.length}. Введите насчитанное; ноль — тоже факт.</Typography.Text>
          <Button
            size="small"
            data-testid="count-book-fill"
            onClick={() => {
              setQuantities((prev) => ({ ...prev, ...Object.fromEntries(uncounted.map((l) => [l.id, Number(l.quantity_plan)])) }));
              changed();
            }}
          >
            Подставить учётное в пустые строки
          </Button>
        </Space>
      )}
      {narrow ? (
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          {lines.isLoading ? <Typography.Text type="secondary">Загрузка строк…</Typography.Text> : null}
          {(lines.data?.items ?? []).map((line) => {
            const p = packageOf(line);
            const value = quantities[line.id];
            const ratio = p ? Number(p.ratio) : 1;
            return (
              <Card key={line.id} size="small" styles={{ body: { padding: 12 } }}>
                <Typography.Text strong style={{ display: "block", overflowWrap: "anywhere" }}>
                  {line.line_no}. <RefLabel entity="storage_objects" id={line.storage_object_id} />
                </Typography.Text>
                <Typography.Text type="secondary" style={{ display: "block", fontSize: 13 }}>
                  {needsLocation ? "План" : "Учёт"}: {qtyText(line.quantity_plan, line.storage_object_id)} · {needsLocation ? "выполнено" : "факт"}: {doneText(line)}
                  {p ? ` · ${p.name} × ${fmt(p.ratio)}` : ""}
                </Typography.Text>
                {hasColumn("batch_id") && line.batch_id ? (
                  <Typography.Text type="secondary" style={{ display: "block", fontSize: 13 }}>
                    Партия: <RefLabel entity="batches" id={line.batch_id} />
                  </Typography.Text>
                ) : null}
                <label style={{ display: "block", marginTop: 8 }}>
                  <Typography.Text>Сейчас, {unitOf(line.storage_object_id)?.name ?? "ед."}</Typography.Text>
                  <InputNumber
                    min={0}
                    decimalSeparator=","
                    inputMode="decimal"
                    value={value ?? undefined}
                    onChange={(v) => {
                      setQuantities({ ...quantities, [line.id]: v });
                      setLineRefusals(({ [line.id]: _gone, ...rest }) => rest);
                      changed();
                    }}
                    status={problemOf(line) ? "error" : undefined}
                    suffix={unitOf(line.storage_object_id)?.name}
                    style={{ width: "100%", marginTop: 4 }}
                    aria-label={`Сейчас, ${unitOf(line.storage_object_id)?.name ?? "ед."}: строка ${line.line_no}`}
                    aria-invalid={!!problemOf(line)}
                  />
                </label>
                {p && ratio > 1 && value ? (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    = {fmt(value / ratio)} {p.name.toLowerCase()}
                  </Typography.Text>
                ) : null}
                {problemOf(line) ? (
                  <Typography.Text type="danger" role="alert" data-testid={`line-problem-${line.line_no}`} style={{ display: "block", fontSize: 13 }}>
                    {problemOf(line)}
                  </Typography.Text>
                ) : null}
                {spread ? (
                  <div style={{ marginTop: 8 }}>
                    <Typography.Text>Куда</Typography.Text>
                    {lineDestination(line)}
                  </div>
                ) : null}
              </Card>
            );
          })}
        </Space>
      ) : (
      <Table<Line>
        size="small"
        rowKey="id"
        loading={lines.isLoading}
        dataSource={lines.data?.items ?? []}
        pagination={false}
        scroll={{ x: "max-content" }}
        columns={[
          { title: "№", dataIndex: "line_no", width: 50 },
          {
            title: "Товар",
            dataIndex: "storage_object_id",
            // with a cell per line the name wraps, so the cell and the quantity stay on the screen
            render: (v: string) => (spread ? <div style={{ minWidth: 200, maxWidth: 250, whiteSpace: "normal" }}><RefLabel entity="storage_objects" id={v} /></div> : <RefLabel entity="storage_objects" id={v} />),
          },
          ...(spread ? [{ title: "Куда", render: (_: unknown, line: Line) => lineDestination(line, 360) }] : []),
          ...(hasColumn("location_id") ? [{ title: "Ячейка", dataIndex: "location_id", width: 150, render: (v: string | null) => (v ? <RefLabel entity="locations" id={v} /> : "—") }] : []),
          ...(hasColumn("batch_id") ? [{ title: "Партия", dataIndex: "batch_id", width: 130, render: (v: string | null) => (v ? <RefLabel entity="batches" id={v} /> : "—") }] : []),
          ...(hasColumn("state_id") ? [{ title: "Состояние", dataIndex: "state_id", width: 120, render: (v: string | null) => (v ? <RefLabel entity="object_states" id={v} /> : "—") }] : []),
          {
            title: "Упаковка",
            width: spread ? 110 : 170,
            render: (_, line) => {
              const p = packageOf(line);
              if (!p) return <Typography.Text type="secondary">базовые ед.</Typography.Text>;
              return (
                <span>
                  {p.name} × {fmt(p.ratio)}
                  {line.packages_count ? <Typography.Text type="secondary"> · план {fmt(line.packages_count)} уп.</Typography.Text> : null}
                </span>
              );
            },
          },
          { title: needsLocation ? "План" : "Учёт", dataIndex: "quantity_plan", width: 100, render: (v: string, line) => qtyText(v, line.storage_object_id) },
          { title: needsLocation ? "Выполнено" : "Факт", width: 100, render: (_, line) => doneText(line) },
          {
            title: "Сейчас",
            width: spread ? 140 : 190,
            render: (_, line) => {
              const p = packageOf(line);
              const ratio = p ? Number(p.ratio) : 1;
              const value = quantities[line.id];
              return (
                <Space direction="vertical" size={0} style={{ width: "100%" }}>
                  <InputNumber
                    min={0}
                    decimalSeparator=","
                    value={value ?? undefined}
                    onChange={(v) => {
                      setQuantities({ ...quantities, [line.id]: v });
                      setLineRefusals(({ [line.id]: _gone, ...rest }) => rest);
                      changed();
                    }}
                    status={problemOf(line) ? "error" : undefined}
                    suffix={unitOf(line.storage_object_id)?.name}
                    aria-label={`Сейчас, ${unitOf(line.storage_object_id)?.name ?? "ед."}: строка ${line.line_no}`}
                    aria-invalid={!!problemOf(line)}
                    style={{ width: "100%" }}
                  />
                  {p && ratio > 1 && value ? (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      = {fmt(value / ratio)} {p.name.toLowerCase()}
                    </Typography.Text>
                  ) : null}
                  {problemOf(line) ? (
                    <Typography.Text type="danger" role="alert" data-testid={`line-problem-${line.line_no}`} style={{ fontSize: 12, whiteSpace: "normal" }}>
                      {problemOf(line)}
                    </Typography.Text>
                  ) : null}
                </Space>
              );
            },
          },
        ]}
      />
      )}
      {submit.error && <Alert type="error" style={{ marginTop: 12 }} message={describeError(submit.error)} showIcon />}
      {!submit.error && refused ? (
        <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
          Предыдущая попытка не проведена{refused.location ? <> (ячейка <RefLabel entity="locations" id={refused.location} />)</> : null}: {refused.text}
        </Typography.Paragraph>
      ) : null}
      {/* BUG-010: the note and the buttons never share one row on a narrow screen, so the note is not squeezed to a
          column of single letters; on a wide screen they share it and wrap when needed */}
      <div style={{ display: "flex", flexDirection: narrow ? "column" : "row", flexWrap: "wrap", gap: 12, alignItems: narrow ? "stretch" : "center", justifyContent: "space-between", marginTop: 16 }}>
        <Typography.Text type={problems.length ? "danger" : "secondary"} style={{ flex: narrow ? undefined : "1 1 240px", minWidth: 0 }} data-testid="lines-note">
          {problems.length
            ? `Исправьте количество в ${problems.length === 1 ? "строке" : "строках"} ${problems.map((l) => `№${l.line_no}`).join(", ")} — остальное введённое сохранено`
            : "Пустые строки пропускаются; количество — в базовой единице товара"}
        </Typography.Text>
        <Space wrap style={{ justifyContent: narrow ? "stretch" : "flex-end" }}>
          <Button onClick={onClose}>Отмена</Button>
          <Button
            type="primary"
            disabled={(needsLocation && (spread ? chosen.some((line) => !lineLocations[line.id]) : !locationId)) || !filled || problems.length > 0}
            onClick={() => {
              // the confirmation shows the check of this input only — never the refusal of the previous address
              submit.reset();
              setStep("confirm");
            }}
          >
            Проверить и продолжить
          </Button>
        </Space>
      </div>
    </>
  );

  const confirmScreen = (
    <>
      {/* BUG-Z-003: the promise names what this action really does — a count fact changes nothing until approval */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Проверьте, что будет записано."
        description={spread ? "После подтверждения каждая строка будет оприходована в свою ячейку хранения: остатки изменятся сразу" : action.outcome ?? "После подтверждения остатки изменятся."}
      />
      <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}>
        {needsLocation && !spread && (
          <Descriptions.Item label={isShip ? "Откуда" : "Куда"}>
            <RefLabel entity="locations" id={locationId} />
          </Descriptions.Item>
        )}
        {chosen.map((line) => {
          const p = packageOf(line);
          const value = Number(quantities[line.id]);
          const unit = unitOf(line.storage_object_id)?.name;
          // QA1101-B02: «40 шт», «0,5 кг»; a package — «1 коробка × 50 шт = 50 шт»
          return (
            <Descriptions.Item key={line.id} label={`Строка ${line.line_no}`}>
              {objectLabel(line.storage_object_id)} — <b data-testid={`confirm-qty-${line.line_no}`}>{qtyText(value, line.storage_object_id)}</b>
              {p && Number(p.ratio) > 1 ? ` (${fmt(value / Number(p.ratio))} ${p.name.toLowerCase()} × ${fmt(p.ratio)}${unit ? ` ${unit}` : ""} = ${qtyText(value, line.storage_object_id)})` : ""}
              {line.batch_id ? (
                <>
                  {" "}
                  · партия <RefLabel entity="batches" id={line.batch_id} />
                </>
              ) : null}
              {spread ? (
                <>
                  {" → "}
                  <b>{placeOf[line.id]?.location_id === lineLocations[line.id] && placeOf[line.id]?.place ? placeOf[line.id]?.place : <RefLabel entity="locations" id={lineLocations[line.id]} />}</b>
                </>
              ) : null}
            </Descriptions.Item>
          );
        })}
      </Descriptions>
      {submit.error && <Alert type="error" style={{ marginBottom: 12 }} message={describeError(submit.error)} showIcon />}
      {lost === "checking" && <Alert type="info" showIcon style={{ marginBottom: 12 }} data-testid="action-checking" message="Ответ сервера не получен. Проверяем результат…" />}
      {lost === "done" && (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 12 }}
          data-testid="action-verified"
          message={`${action.label}: выполнено`}
          description="Ответ сервера не дошёл, но факт по строкам уже изменился — повторять не нужно. Проверьте результат на вкладке «Товары» и в истории."
        />
      )}
      {lost === "unchanged" && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          data-testid="action-unchanged"
          message="Не проведено: факт по строкам не изменился"
          description="Ответ сервера не дошёл, и строки остались прежними. Введённое сохранено — подтвердите ещё раз, когда связь будет устойчивой."
        />
      )}
      {lost === "offline" && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          data-testid="action-unknown"
          message="Результат пока неизвестен: нет связи с сервером"
          description="Операция могла пройти. Не подтверждайте её снова: когда связь появится, нажмите «Проверить результат»."
        />
      )}
      <Space style={{ width: "100%", justifyContent: "flex-end" }}>
        {lost === "done" ? (
          <Button type="primary" onClick={onClose}>
            Готово
          </Button>
        ) : lost === "offline" || lost === "checking" ? (
          <Button type="primary" loading={lost === "checking"} onClick={() => void checkLost()} data-testid="action-recheck">
            Проверить результат
          </Button>
        ) : (
          <>
            <Button onClick={() => setStep("edit")}>Назад</Button>
            <Button
              type="primary"
              loading={submit.isPending}
              onClick={() => {
                setLost(null);
                setFactBefore(factOf(lines.data?.items));
                submit.mutate();
              }}
            >
              Подтвердить: {action.label}
            </Button>
          </>
        )}
      </Space>
    </>
  );

  const doneScreen = result && (
    <>
      <Alert
        type="success"
        showIcon
        style={{ marginBottom: 12 }}
        message={
          <span>
            Готово. Статус документа: <Tag color="blue">{statusLabel(result.status)}</Tag>
            {typeof result.movements === "number" ? ` движений по остаткам: ${result.movements}` : null}
          </span>
        }
        description={result.next_step ?? undefined}
      />
      {result.details && result.details.length > 0 && (
        <Table
          size="small"
          rowKey={(r) => String(r.line_no)}
          pagination={false}
          dataSource={result.details}
          scroll={{ x: "max-content" }}
          columns={[
            { title: "№", dataIndex: "line_no", width: 50 },
            { title: "Товар", dataIndex: "object", render: (v: string | undefined, r) => v ?? `строка ${r.line_no}` },
            { title: "Ячейка", dataIndex: "location" },
            { title: "Количество", dataIndex: "quantity", width: 120, render: (v: string, r) => (r.unit ? `${fmt(v)} ${r.unit}` : fmt(v)) },
            // BUG-R31-012: the batch column names batches; the fact of the line has its own column
            ...(result.details.some((r) => r.batch || r.allocations?.length)
              ? [
                  {
                    title: "Партия",
                    render: (_: unknown, r: (typeof result.details)[number]) =>
                      r.allocations?.length ? r.allocations.map((a) => `${a.batch}: ${fmt(a.quantity)}${r.unit ? ` ${r.unit}` : ""}`).join("; ") : r.batch ?? "без партии",
                  },
                ]
              : []),
            ...(result.details.some((r) => r.fact)
              ? [{ title: "Факт / план строки", width: 160, render: (_: unknown, r: (typeof result.details)[number]) => (r.fact ? `${fmt(r.fact)} из ${fmt(r.plan)}${r.unit ? ` ${r.unit}` : ""}` : "—") }]
              : []),
          ]}
        />
      )}
      {result.cancelled_tasks && result.cancelled_tasks.length > 0 && (
        <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
          Отменены задачи отбора, резервировавшие этот остаток: {result.cancelled_tasks.join(", ")}
        </Typography.Paragraph>
      )}
      <Space style={{ width: "100%", justifyContent: "flex-end", marginTop: 16 }}>
        <Button type="primary" onClick={onClose}>
          Готово
        </Button>
      </Space>
    </>
  );

  // a count line has its cell and state as well: the wide dialog keeps «Сейчас» on the screen (QA0211)
  return (
    <Modal title={step === "confirm" ? `${action.label}: подтверждение` : step === "done" ? `${action.label}: результат` : action.label} open onCancel={onClose} footer={null} width={narrow ? "calc(100vw - 16px)" : (spread || !needsLocation) && step === "edit" ? 1180 : 900} style={narrow ? { top: 8, maxWidth: "calc(100vw - 16px)" } : undefined} destroyOnHidden>
      {step === "edit" ? editScreen : step === "confirm" ? confirmScreen : doneScreen}
    </Modal>
  );
}
